/**
 * Evaluates a media query and resolves a promise based on the result.
 *
 * @param {Object} options - The options object.
 * @param {string} options.query - The media query string to evaluate.
 * @returns {Promise<boolean>} A promise that resolves to true if the media query matches, otherwise resolves when the media query changes to match.
 */
function media({ query }) {
  const mediaQuery = globalThis.matchMedia(query);
  return new Promise(resolve => {
    if (mediaQuery.matches) {
      resolve(true);
    } else {
      mediaQuery.addEventListener('change', resolve, { once: true });
    }
  });
}

/**
 * Observes an element and resolves a promise when the element becomes visible in the viewport.
 *
 * @param {Object} param - The parameter object.
 * @param {HTMLElement} param.element - The DOM element to observe.
 * @returns {Promise<boolean>} A promise that resolves to true when the element is visible.
 */
function visible({ element }) {
  return new Promise(resolve => {
    const observer = new globalThis.IntersectionObserver(async entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observer.disconnect();
          resolve(true);
          break;
        }
      }
    });
    observer.observe(element);
  });
}

/**
 * Returns a promise that resolves when the browser is idle.
 * Uses `requestIdleCallback` if available, otherwise falls back to `setTimeout`.
 *
 * @returns {Promise<void>} A promise that resolves when the browser is idle.
 */
function idle() {
  return new Promise(resolve => {
    if ('requestIdleCallback' in globalThis) {
      globalThis.requestIdleCallback(resolve);
    } else {
      setTimeout(resolve, 200);
    }
  });
}

export const islands = import.meta.glob('/modules/**/*.{js,jsx}');

/**
 * Observes the DOM for added nodes and revives custom elements based on specific attributes.
 *
 * @param {Object} islands - An object containing module paths as keys and their corresponding functions as values.
 *
 * The function performs the following tasks:
 * 1. Sets up a MutationObserver to watch for added nodes in the DOM.
 * 2. For each added node, it performs a depth-first search (DFS) to check if the node is a potential custom element.
 * 3. If the node is a custom element and has specific attributes (`client:visible`, `client:media`, `client:idle`), it performs corresponding actions.
 * 4. Invokes the module function associated with the custom element if it exists in the `islands` object.
 *
 * @example
 * const islands = {
 *   '/modules/my-element/my-element.js': () => { /* module code *\/ },
 *   '/modules/another-element/another-element.jsx': () => { /* module code *\/ }
 * };
 * revive(islands);
 */
export function revive(islands) {
  const observer = new globalThis.MutationObserver(mutations => {
    for (const { addedNodes } of mutations) {
      for (const node of addedNodes) {
        if (node.nodeType === 1) dfs(node);
      }
    }
  });

  async function dfs(node) {
    const tagName = node.tagName.toLowerCase();
    const potentialJsPath = `/modules/${tagName}/${tagName}.js`;
    const potentialJsxPath = `/modules/${tagName}/${tagName}.jsx`;
    const isPotentialCustomElementName = /-/.test(tagName);

    let child = node.firstElementChild;

    while (child) {
      dfs(child);
      child = child.nextElementSibling;
    }

    if (
      isPotentialCustomElementName &&
      (islands[potentialJsPath] || islands[potentialJsxPath])
    ) {
      if (node.hasAttribute('client:visible')) {
        await visible({ element: node });
      }

      const clientMedia = node.getAttribute('client:media');
      if (clientMedia) {
        await media({ query: clientMedia });
      }

      if (node.hasAttribute('client:idle')) {
        await idle();
      }

      // If the islands array contains a module function for the custom element, invoke it
      islands[potentialJsPath]
        ? islands[potentialJsPath]()
        : islands[potentialJsxPath]();
    }
  }

  dfs(document.body);

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}
